import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import * as BUILD_CAPABILITIES from '#pplx/frontend/deployment-capabilities/build.js';
('use client');
import { useGdprConsent } from '#pplx/frontend/analytics/browser/useGdprConsent.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { SafeLocalStorage } from '#pplx/frontend/browser-storage/localStorage.js';
import { isCometBrowser } from '#pplx/frontend/comet/client/utils.js';
import { LOGIN_NEW_PARAM, LOGIN_SOURCE_PARAM, TRACKED_SINGULAR_USER_SUBSCRIBED_PAYING_KEY, } from '#pplx/frontend/components/utils/consts.js';
import { PPLX_VISITOR_ID_COOKIE } from '#pplx/frontend/cookies/constants.js';
import { getCookieCometQueryCount, getCookieCometQueryCountLoggedIn, getQueryCountVisitorId, } from '#pplx/frontend/cookies/metadataCookieManager.js';
import { getCookie } from '#pplx/frontend/cookies/provider.js';
import { sha256Hex, _generateMatchId, startCometSession, sendCometEvent, SingularAttributionContext, NTH_QUERY_TO_EVENT_NAME, NTH_COMET_QUERY_TO_EVENT_NAME, TEST_PRODUCT_ID, PROD_PRODUCT_ID, getEventId, SINGULAR_API_KEY, SINGULAR_API_TOKEN, TRACKED_SINGULAR_CONNECTOR_ACTIVATED_KEY_PREFIX, initSingular, } from '#pplx/frontend/growth/analytics-singular/SingularAttributionProviderShared.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
// match implementation as backend singular_utils.py
// must match attribution/singular_utils.py
// https://www.notion.so/perplexity-ai/Singular-Attribution-1836172b229b80a29327c9301e57fc7a?showMoveTo=true&saveParent=true
const EnabledSingularAttributionProvider = ({ children }) => {
    const { session } = useSession();
    const { hasConsent, hasTrackingPolicyResolved } = useGdprConsent();
    const visitorId = getCookie(PPLX_VISITOR_ID_COOKIE);
    const { env: { isProduction, isStaging }, device: { isMobile }, } = useWebPlatform();
    const enqueuedEvents = useRef([]);
    const emittedEvents = useRef([]);
    const apiOrgAttributionEvents = useRef(null);
    const singularSdkOperationQueue = useRef(Promise.resolve());
    const singularSdkInitialization = useRef(null);
    const singularSdkInitializationKey = useRef(null);
    const pendingApiMatch = useRef(null);
    const activeMatchId = useRef(null);
    const query = useSearchParams();
    const apiAttributionConsent = hasTrackingPolicyResolved ? hasConsent : null;
    // Checkout callbacks can resume after a session refresh. Read committed consent at
    // invocation and again after async work instead of trusting the callback's closure.
    const subscriptionConsent = useRef({
        generation: 0,
        hasConsent,
        hasTrackingPolicyResolved,
        userId: session?.user?.id,
        visitorId,
    });
    useLayoutEffect(() => {
        const previous = subscriptionConsent.current;
        const invalidated = previous.userId !== session?.user?.id ||
            previous.visitorId !== visitorId ||
            (hasTrackingPolicyResolved && !hasConsent);
        subscriptionConsent.current = {
            generation: previous.generation + (invalidated ? 1 : 0),
            hasConsent,
            hasTrackingPolicyResolved,
            userId: session?.user?.id,
            visitorId,
        };
    }, [hasConsent, hasTrackingPolicyResolved, session?.user?.id, visitorId]);
    // wrap sdk state in an object since singularSdk is a function
    // which is incompatible with setState
    const [singularSdk, setSingularSdk] = useState(undefined);
    const singularSdkRef = useRef(undefined);
    const [isInitializedWithUserId, setIsInitializedWithUserId] = useState(false);
    const [emailHash, setEmailHash] = useState(null);
    const runSingularSdkOperation = useCallback((operation) => {
        const result = singularSdkOperationQueue.current.then(operation, operation);
        singularSdkOperationQueue.current = result.catch(() => { });
        return result;
    }, []);
    const getApiOrgAttributionEvents = useCallback(() => {
        apiOrgAttributionEvents.current ??=
            import('#pplx/frontend/growth/analytics-singular/apiOrgAttributionEvents.js').then(({ createApiOrgAttributionEvents }) => createApiOrgAttributionEvents());
        return apiOrgAttributionEvents.current;
    }, []);
    // pre-compute SHA256 hash of user email for Singular ehash field
    useEffect(() => {
        const email = session?.user?.email;
        if (!email) {
            setEmailHash(null);
            return;
        }
        sha256Hex(email).then(setEmailHash);
    }, [session?.user?.email]);
    const initializeSingular = useCallback(() => {
        if (!hasConsent)
            return null;
        const userId = session?.user?.id;
        const productId = isProduction || isStaging ? PROD_PRODUCT_ID : TEST_PRODUCT_ID;
        if (!visitorId)
            return null;
        const initializationKey = `${productId}:${visitorId}:${userId ?? ''}`;
        if (singularSdkInitializationKey.current === initializationKey) {
            return singularSdkInitialization.current;
        }
        const initialization = initSingular({ userId, productId, visitorId });
        singularSdkInitializationKey.current = initializationKey;
        singularSdkInitialization.current = initialization;
        void initialization.then(({ SingularConfig, singularSdk: sdk }) => {
            if (singularSdkInitializationKey.current !== initializationKey)
                return;
            const initializedSdk = { SingularConfig, sdk };
            singularSdkRef.current = initializedSdk;
            setSingularSdk(initializedSdk);
            if (userId) {
                setIsInitializedWithUserId(true);
            }
            if (!userId || !isCometBrowser())
                return;
            const cometQueryCount = getCookieCometQueryCount();
            // only start comet s2s session if not yet activated on comet
            if (cometQueryCount > 1)
                return;
            startCometSession({ reason: 'comet attribution', hasConsent });
        });
        return initialization;
    }, [hasConsent, isProduction, isStaging, session?.user?.id, visitorId]);
    useEffect(() => {
        void initializeSingular();
    }, [initializeSingular]);
    const _sendEventSingular = useCallback(async (eventName, extraArguments, options) => {
        const dedupe = options?.dedupe ?? true;
        const includeEmailHash = options?.includeEmailHash ?? true;
        const sessionUserId = session?.user?.id;
        const generation = subscriptionConsent.current.generation;
        const enqueue = () => {
            enqueuedEvents.current.push({
                name: eventName,
                extraArguments,
                ...options,
                dedupe,
                includeEmailHash,
                sessionUserId,
                visitorId,
            });
        };
        const canSendSubscription = () => {
            const current = subscriptionConsent.current;
            if (current.generation !== generation ||
                current.userId !== sessionUserId ||
                current.visitorId !== visitorId)
                return false;
            if (!current.hasTrackingPolicyResolved) {
                enqueue();
                return false;
            }
            return current.hasConsent;
        };
        if (options?.waitForConsent ? !canSendSubscription() : !hasConsent)
            return;
        const activeSingularSdk = singularSdkRef.current;
        if (!activeSingularSdk) {
            enqueue();
            return;
        }
        if (!visitorId) {
            return;
        }
        if (options?.customUserId && !sessionUserId) {
            return;
        }
        if (options?.singularDeviceId && !options.customUserId)
            return;
        const eventId = await getEventId(visitorId, eventName);
        const eventPayload = {
            // Session emailHash first so an explicit ehash in extraArguments
            // (e.g. a lead's submitted email) can override it.
            ...(includeEmailHash && emailHash ? { ehash: emailHash } : {}),
            ...extraArguments,
            visitorId,
            eventId,
            isMobile,
        };
        await runSingularSdkOperation(async () => {
            if (options?.waitForConsent && !canSendSubscription())
                return;
            const wasEmitted = emittedEvents.current.some((event) => event.name === eventName &&
                event.customUserId === options?.customUserId &&
                event.dedupeKey === options?.dedupeKey);
            if (dedupe && wasEmitted)
                return;
            if (options?.singularDeviceId) {
                if (!options.customUserId)
                    return;
                if (!options.temporaryMatchId)
                    return;
                if (!sessionUserId)
                    return;
                // The SDK has no per-event sdid option. Reinitializing the same app only
                // updates its config; event() snapshots the org identity before we restore it.
                const productId = isProduction || isStaging ? PROD_PRODUCT_ID : TEST_PRODUCT_ID;
                const { sendSingularEventWithTemporaryIdentity } = await import('#pplx/frontend/growth/analytics-singular/sendSingularEventWithTemporaryIdentity.js');
                const didSend = sendSingularEventWithTemporaryIdentity({
                    runtime: activeSingularSdk,
                    sdkKey: SINGULAR_API_KEY,
                    sdkToken: SINGULAR_API_TOKEN,
                    productId,
                    eventName,
                    eventPayload,
                    singularDeviceId: options.singularDeviceId,
                    customUserId: options.customUserId,
                    temporaryMatchId: options.temporaryMatchId,
                    sessionUserId,
                    sessionMatchId: activeMatchId.current,
                });
                if (!didSend)
                    return;
            }
            else if (options?.customUserId) {
                activeSingularSdk.sdk.login(options.customUserId);
                activeSingularSdk.sdk.event(eventName, eventPayload);
                if (sessionUserId) {
                    activeSingularSdk.sdk.login(sessionUserId);
                }
            }
            else {
                activeSingularSdk.sdk.event(eventName, eventPayload);
            }
            if (dedupe) {
                emittedEvents.current.push({
                    name: eventName,
                    customUserId: options?.customUserId,
                    dedupeKey: options?.dedupeKey,
                });
            }
        });
    }, [
        hasConsent,
        visitorId,
        isMobile,
        emailHash,
        isProduction,
        isStaging,
        session?.user?.id,
        runSingularSdkOperation,
    ]);
    /**
     * Singular attribution for advertisers does not support aggregate counts
     * So, we define individual events for certain query count thresholds
     * Advertisers can target and optimize for the relevant event by name
     */
    const maybeSendNthQueryEventSingular = useCallback(async ({ isCometBrowser }) => {
        const totalQueryCount = getQueryCountVisitorId();
        const cometQueryCount = getCookieCometQueryCount();
        const cometQueryCountLoggedIn = getCookieCometQueryCountLoggedIn();
        if (totalQueryCount && NTH_QUERY_TO_EVENT_NAME[totalQueryCount]) {
            await _sendEventSingular(NTH_QUERY_TO_EVENT_NAME[totalQueryCount]);
        }
        if (cometQueryCount && NTH_COMET_QUERY_TO_EVENT_NAME[cometQueryCount]) {
            await _sendEventSingular(NTH_COMET_QUERY_TO_EVENT_NAME[cometQueryCount]);
        }
        if (isCometBrowser && cometQueryCountLoggedIn === 1) {
            // trigger comet activated s2s event if first logged in comet query
            sendCometEvent({
                eventName: 'activated',
                reason: 'comet attribution',
                hasConsent,
            });
        }
    }, [_sendEventSingular, hasConsent]);
    const setMatchIdWithEmail = useCallback(async (email) => {
        if (!hasConsent)
            return;
        if (!singularSdk)
            return;
        const matchId = await _generateMatchId(email);
        activeMatchId.current = matchId;
        await runSingularSdkOperation(() => singularSdk.sdk.setMatchID(matchId));
    }, [hasConsent, runSingularSdkOperation, singularSdk]);
    const resolveApiAttribution = useCallback(async () => {
        if (!hasTrackingPolicyResolved)
            return null;
        if (!hasConsent) {
            return { matchId: null, consent: false };
        }
        const email = session?.user?.email;
        if (!email)
            return null;
        const matchId = await _generateMatchId(email);
        pendingApiMatch.current = { email, matchId };
        activeMatchId.current = matchId;
        if (singularSdk) {
            await runSingularSdkOperation(() => singularSdk.sdk.setMatchID(matchId));
        }
        return { matchId, consent: true };
    }, [
        hasConsent,
        hasTrackingPolicyResolved,
        runSingularSdkOperation,
        session?.user?.email,
        singularSdk,
    ]);
    useEffect(() => {
        const pendingMatch = pendingApiMatch.current;
        if (!singularSdk || !pendingMatch || pendingMatch.email !== session?.user?.email)
            return;
        void runSingularSdkOperation(() => singularSdk.sdk.setMatchID(pendingMatch.matchId));
    }, [runSingularSdkOperation, session?.user?.email, singularSdk]);
    useEffect(() => {
        if (hasConsent)
            return;
        pendingApiMatch.current = null;
        activeMatchId.current = null;
    }, [hasConsent]);
    const sendApiOrgCreditsAddedEvent = useCallback((apiOrgId, apiOrgEmailHash) => _sendEventSingular('api credits added', { customer_external_id: apiOrgId, ehash: apiOrgEmailHash }, {
        customUserId: apiOrgId,
        singularDeviceId: apiOrgId,
        temporaryMatchId: `match_${apiOrgEmailHash}`,
    }), [_sendEventSingular]);
    const setApiOrgEmailHash = useCallback((apiOrgId, apiOrgEmailHash) => getApiOrgAttributionEvents().then((events) => events.resolve(apiOrgId, apiOrgEmailHash, sendApiOrgCreditsAddedEvent)), [getApiOrgAttributionEvents, sendApiOrgCreditsAddedEvent]);
    const beginApiOrgAttributionRevalidation = useCallback((apiOrgId) => {
        void getApiOrgAttributionEvents().then((events) => events.beginRevalidation(apiOrgId), () => { });
    }, [getApiOrgAttributionEvents]);
    const sendApiCreditsAddedEventSingular = useCallback((apiOrgId) => getApiOrgAttributionEvents().then((events) => events.request(apiOrgId, sendApiOrgCreditsAddedEvent)), [getApiOrgAttributionEvents, sendApiOrgCreditsAddedEvent]);
    const sendMarketingEmailCollectedEventSingular = useCallback(async (email) => {
        await setMatchIdWithEmail(email);
        const ehash = await sha256Hex(email);
        await _sendEventSingular('enterprise demo email collected', { ehash });
    }, [setMatchIdWithEmail, _sendEventSingular]);
    // Lead-form conversion: set the Meta match-id + ehash from the lead's submitted
    // email so it attributes even for anonymous (logged-out) visitors.
    const sendMarketingFormCompletedEventSingular = useCallback(async (email) => {
        // Await so the match-id is set before the event fires, otherwise the two
        // run on separate microtask chains and the event can race ahead unattributed.
        await setMatchIdWithEmail(email);
        const ehash = await sha256Hex(email);
        await _sendEventSingular('enterprise demo form completed', { ehash });
    }, [setMatchIdWithEmail, _sendEventSingular]);
    const sendMarketingLargeOrgFormCompletedEventSingular = useCallback(async (email) => {
        // Await so the match-id is set before the event fires, otherwise the two
        // run on separate microtask chains and the event can race ahead unattributed.
        await setMatchIdWithEmail(email);
        const ehash = await sha256Hex(email);
        await _sendEventSingular('enterprise demo form completed large org', { ehash });
    }, [setMatchIdWithEmail, _sendEventSingular]);
    // Primary Meta optimization signal: a booked meeting from the lead form.
    // Only the hashed email (ehash/match-id) and the non-PII meeting duration are
    // sent — attendee/time/meetingId are deliberately not forwarded to avoid
    // shipping PII downstream to Singular/Meta.
    const sendMarketingMeetingBookedEventSingular = useCallback(async (payload) => {
        if (!payload.email)
            return;
        // Await so the match-id lands before the event fires (see form-completed note).
        await setMatchIdWithEmail(payload.email);
        const extraArguments = { ehash: await sha256Hex(payload.email) };
        if (payload.duration != null)
            extraArguments.duration = String(payload.duration);
        await _sendEventSingular('enterprise demo meeting booked', extraArguments);
    }, [setMatchIdWithEmail, _sendEventSingular]);
    const sendSmbEmailCollectedEventSingular = useCallback(async (email) => {
        await setMatchIdWithEmail(email);
        const ehash = await sha256Hex(email);
        await _sendEventSingular('smb email collected', { ehash }, { dedupe: false });
    }, [setMatchIdWithEmail, _sendEventSingular]);
    const sendBuildersEmailCollectedEventSingular = useCallback(async (email) => {
        await setMatchIdWithEmail(email);
        const ehash = await sha256Hex(email);
        await _sendEventSingular('builders email collected', { ehash }, { dedupe: false });
    }, [setMatchIdWithEmail, _sendEventSingular]);
    const sendComputerEmailCollectedEventSingular = useCallback(async (email) => {
        await setMatchIdWithEmail(email);
        const ehash = await sha256Hex(email);
        await _sendEventSingular('computer email collected', { ehash }, { dedupe: false });
    }, [setMatchIdWithEmail, _sendEventSingular]);
    const sendStudentEmailCollectedEventSingular = useCallback(async (email) => {
        await setMatchIdWithEmail(email);
        const ehash = await sha256Hex(email);
        await _sendEventSingular('student email collected', { ehash }, { dedupe: false });
    }, [setMatchIdWithEmail, _sendEventSingular]);
    // Hero email-form conversion, shared by the enterprise and finance landing pages.
    // Sets the Meta match-id + ehash from the submitted email so it attributes even
    // for anonymous (logged-out) visitors.
    const sendDemoEmailEnteredEventSingular = useCallback(async (email) => {
        await setMatchIdWithEmail(email);
        const ehash = await sha256Hex(email);
        await _sendEventSingular('enterprise demo email entered', { ehash });
    }, [setMatchIdWithEmail, _sendEventSingular]);
    // Non-email "Get Started" CTAs, shared by the enterprise and finance landing
    // pages. No email is available, so this is a CTA-intent signal only (no ehash).
    const sendDemoCtaClickedEventSingular = useCallback(() => _sendEventSingular('enterprise demo cta clicked'), [_sendEventSingular]);
    const sendApiConsoleCtaClickedEventSingular = useCallback(() => _sendEventSingular('api console cta clicked'), [_sendEventSingular]);
    const sendConnectorActivatedEventSingular = useCallback(async (connectorName) => {
        if (!hasConsent || !visitorId)
            return;
        const storageKey = `${TRACKED_SINGULAR_CONNECTOR_ACTIVATED_KEY_PREFIX}:${session?.user?.id ?? visitorId}`;
        if (SafeLocalStorage.getItem(storageKey))
            return;
        if (!isInitializedWithUserId)
            return;
        await _sendEventSingular('connector activated', { connectorName });
        SafeLocalStorage.setItem(storageKey, 'true');
    }, [hasConsent, isInitializedWithUserId, session?.user?.id, visitorId, _sendEventSingular]);
    const sendSlackInstallClickEventSingular = useCallback(async () => {
        if (!hasConsent || !visitorId)
            return false;
        const initialization = initializeSingular();
        if (!initialization)
            return false;
        await initialization;
        await _sendEventSingular('slack install click', undefined, {
            dedupe: false,
            includeEmailHash: false,
        });
        return true;
    }, [_sendEventSingular, hasConsent, initializeSingular, visitorId]);
    const sendSignedInEventSingular = useCallback(async (isNewUser) => {
        if (!hasConsent)
            return;
        await _sendEventSingular('user sign in', { isNewUser });
        if (!session?.user?.id)
            return;
        if (!singularSdk)
            return;
        singularSdk.sdk.login(session.user.id);
    }, [hasConsent, session?.user?.id, singularSdk, _sendEventSingular]);
    // dynamic import and initialization takes time
    // enqueue events and emit once ready
    useEffect(() => {
        if (hasConsent && singularSdk && enqueuedEvents.current.length > 0) {
            const currentEvents = enqueuedEvents.current.filter((event) => !event.waitForConsent ||
                (event.sessionUserId === session?.user?.id && event.visitorId === visitorId));
            const dedupedEnqueuedEvents = currentEvents.filter((event, index, self) => !event.dedupe ||
                index ===
                    self.findIndex((candidate) => candidate.name === event.name &&
                        candidate.customUserId === event.customUserId &&
                        candidate.dedupeKey === event.dedupeKey));
            dedupedEnqueuedEvents.forEach(({ name, extraArguments, customUserId, singularDeviceId, temporaryMatchId, dedupe, includeEmailHash, dedupeKey, waitForConsent, }) => {
                _sendEventSingular(name, extraArguments, {
                    customUserId,
                    singularDeviceId,
                    temporaryMatchId,
                    dedupe,
                    includeEmailHash,
                    dedupeKey,
                    waitForConsent,
                });
            });
            enqueuedEvents.current = [];
        }
    }, [hasConsent, singularSdk, _sendEventSingular, session?.user?.id, visitorId]);
    // Retain subscription conversions only during policy loading, never across a
    // resolved denial or an account/visitor change.
    useEffect(() => {
        enqueuedEvents.current = enqueuedEvents.current.filter((event) => {
            if (event.waitForConsent) {
                return (event.sessionUserId === session?.user?.id &&
                    event.visitorId === visitorId &&
                    (!hasTrackingPolicyResolved || hasConsent));
            }
            return hasConsent;
        });
    }, [hasConsent, hasTrackingPolicyResolved, session?.user?.id, visitorId]);
    useEffect(() => {
        /**
         * track user sign in events according to URL params
         * sign in occurs server-side so we use page visit with login params as proxy
         */
        if (query?.get(LOGIN_SOURCE_PARAM) && query?.get(LOGIN_NEW_PARAM)) {
            const isNewUser = query?.get(LOGIN_NEW_PARAM) === 'true';
            sendSignedInEventSingular(isNewUser);
            if (isNewUser) {
                _sendEventSingular('new user sign in');
                if (session?.user?.email) {
                    // set match id for logged in users according to session
                    setMatchIdWithEmail(session.user.email);
                }
            }
            else {
                _sendEventSingular('existing user sign in');
            }
            if (isCometBrowser()) {
                _sendEventSingular('comet user sign in');
            }
        }
    }, [
        query,
        sendSignedInEventSingular,
        _sendEventSingular,
        session?.user?.email,
        setMatchIdWithEmail,
    ]);
    useEffect(() => {
        /**
         * if user has a subscription and user is paying for it
         * send "user subscribed paying" event if not already sent (according to LS)
         * session data upon subscription change in FE isn't reliable enough
         *
         * wait for SDK to be initialized with userId to ensure events
         * are attributed to the correct user
         */
        if (!isInitializedWithUserId)
            return;
        if (session?.user?.subscription_tier && session?.user?.payment_tier === 'paid') {
            if (!SafeLocalStorage.getItem(TRACKED_SINGULAR_USER_SUBSCRIBED_PAYING_KEY)) {
                _sendEventSingular('user subscribed paying');
                SafeLocalStorage.setItem(TRACKED_SINGULAR_USER_SUBSCRIBED_PAYING_KEY, 'true');
            }
        }
    }, [
        isInitializedWithUserId,
        session?.user?.subscription_tier,
        session?.user?.payment_tier,
        _sendEventSingular,
    ]);
    return (_jsx(SingularAttributionContext.Provider, { value: useMemo(() => ({
            sendQueryEventSingular: () => _sendEventSingular('query submitted'),
            maybeSendNthQueryEventSingular,
            sendSubscribedEventSingular: () => _sendEventSingular('user subscribed', undefined, { waitForConsent: true }),
            sendAppDownloadClickedEventSingular: (origin) => _sendEventSingular('app download button clicked', { origin }),
            sendResurrectedActivationEventSingular: () => _sendEventSingular('resurrected activated'),
            sendCometDownloadPageLoadEventSingular: (page) => _sendEventSingular(`${page} page loaded`),
            sendCometDownloadPageSignInEventSingular: (page) => _sendEventSingular(`${page} page signed in`),
            sendCometDownloadedEventSingular: () => _sendEventSingular('download-comet comet downloaded'),
            sendCometDownloadButtonClickedEventSingular: (page) => _sendEventSingular(`${page} download button clicked`),
            sendCometWaitlistButtonClickedEventSingular: (page) => _sendEventSingular(`${page} waitlist button clicked`),
            sendCometDownloadLinkEmailEventSingular: (page) => _sendEventSingular(`${page} send download link`),
            sendSubscribedMaxEventSingular: () => _sendEventSingular('user subscribed max', undefined, { waitForConsent: true }),
            sendSubscribedPayingEnterpriseEventSingular: () => _sendEventSingular('user subscribed paying enterprise', undefined, {
                waitForConsent: true,
            }),
            sendAssistantConnectorClickedEventSingular: (connectorType) => _sendEventSingular('assistant connector clicked', {
                connectorType,
            }),
            sendAssistantPaywallPageVisitEventSingular: () => _sendEventSingular('assistant paywall page visit'),
            sendAssistantStartFreeTrialClickedEventSingular: () => _sendEventSingular('assistant start trial clicked'),
            sendAssistantUpgradeToMaxClickedEventSingular: () => _sendEventSingular('assistant upgrade max clicked'),
            sendAssistantUpgradeToProClickedEventSingular: () => _sendEventSingular('assistant upgrade pro clicked'),
            sendAssistantPaywallDismissedEventSingular: () => _sendEventSingular('assistant paywall dismissed'),
            sendAssistantConnectPageVisitEventSingular: () => _sendEventSingular('assistant connect page visit'),
            sendAssistantGreetingPageVisitEventSingular: () => _sendEventSingular('assistant greeting page visit'),
            sendAssistantCalendarSelectionPageVisitEventSingular: () => _sendEventSingular('assistant cal selection visit'),
            sendAssistantTimeSettingsPageVisitEventSingular: () => _sendEventSingular('assistant time settings visit'),
            sendAssistantAutoLabelPageVisitEventSingular: () => _sendEventSingular('assistant auto label page visit'),
            sendAssistantAutoDraftPageVisitEventSingular: () => _sendEventSingular('assistant auto draft page visit'),
            sendAssistantCompletePageVisitEventSingular: () => _sendEventSingular('assistant complete page visit'),
            // Study Hub Singular Events
            sendStudyHubPageVisitedEventSingular: () => _sendEventSingular('study hub page visited'),
            sendChatWithTutorClickedEventSingular: () => _sendEventSingular('chat with tutor clicked'),
            sendPracticeQuestionsClickedEventSingular: () => _sendEventSingular('practice questions clicked'),
            sendStudyGuideClickedEventSingular: () => _sendEventSingular('study guide clicked'),
            sendFlashcardsClickedEventSingular: () => _sendEventSingular('flashcards clicked'),
            sendOnePagerClickedEventSingular: () => _sendEventSingular('one pager clicked'),
            sendSpacedRepetitionClickedEventSingular: () => _sendEventSingular('spaced repetition clicked'),
            sendMaterialsUploadedEventSingular: () => _sendEventSingular('materials uploaded'),
            sendStudyHubActivatedEventSingular: () => _sendEventSingular('study hub activated'),
            sendThirdStudyHubEngagementEventSingular: () => _sendEventSingular('third study hub engagement'),
            sendFifthStudyHubEngagementEventSingular: () => _sendEventSingular('fifth study hub engagement'),
            sendTenthStudyHubEngagementEventSingular: () => _sendEventSingular('tenth study hub engagement'),
            sendTryComputerClickedEventSingular: () => _sendEventSingular('try computer clicked'),
            sendCheckoutInitiatedEventSingular: () => _sendEventSingular('checkout initiated'),
            sendActivatedComputerEventSingular: () => _sendEventSingular('activated computer'),
            sendConnectorActivatedEventSingular,
            sendSlackAcquisitionLandingPageViewedEventSingular: () => _sendEventSingular('slack acquisition landing page viewed'),
            sendSlackAcquisitionCtaSelectedEventSingular: (placement) => _sendEventSingular('slack acquisition cta selected', { placement }, { dedupe: false }),
            sendSlackInstallClickEventSingular,
            sendUserConnectedPortfolioEventSingular: () => _sendEventSingular('user connected portfolio'),
            // API Console funnel events
            sendApiOrgCreatedEventSingular: () => _sendEventSingular('api org created'),
            sendApiCreditsAddedEventSingular,
            sendApiKeyCreatedEventSingular: () => _sendEventSingular('api key created'),
            sendApiPaymentMethodAddedEventSingular: () => _sendEventSingular('api payment method added'),
            apiAttributionConsent,
            resolveApiAttribution,
            beginApiOrgAttributionRevalidation,
            setApiOrgEmailHash,
            sendMarketingEmailCollectedEventSingular,
            sendMarketingFormCompletedEventSingular,
            sendMarketingLargeOrgFormCompletedEventSingular,
            sendMarketingMeetingBookedEventSingular,
            sendSmbEmailCollectedEventSingular,
            sendBuildersEmailCollectedEventSingular,
            sendComputerEmailCollectedEventSingular,
            sendStudentLandingPageViewedEventSingular: () => _sendEventSingular('student landing page viewed'),
            sendStudentEmailCollectedEventSingular,
            sendDemoEmailEnteredEventSingular,
            sendDemoCtaClickedEventSingular,
            sendApiConsoleCtaClickedEventSingular,
            sendGatedAssetEventSingular: (eventName, campaignId) => _sendEventSingular(eventName, { campaignId }, { includeEmailHash: false, dedupeKey: campaignId }),
            setMatchIdWithEmail,
        }), [
            _sendEventSingular,
            maybeSendNthQueryEventSingular,
            sendMarketingEmailCollectedEventSingular,
            sendMarketingFormCompletedEventSingular,
            sendMarketingLargeOrgFormCompletedEventSingular,
            sendMarketingMeetingBookedEventSingular,
            sendSmbEmailCollectedEventSingular,
            sendBuildersEmailCollectedEventSingular,
            sendComputerEmailCollectedEventSingular,
            sendStudentEmailCollectedEventSingular,
            sendDemoEmailEnteredEventSingular,
            sendDemoCtaClickedEventSingular,
            sendApiConsoleCtaClickedEventSingular,
            sendConnectorActivatedEventSingular,
            sendSlackInstallClickEventSingular,
            sendApiCreditsAddedEventSingular,
            apiAttributionConsent,
            resolveApiAttribution,
            beginApiOrgAttributionRevalidation,
            setApiOrgEmailHash,
            setMatchIdWithEmail,
        ]), children: children }));
};
export const SingularAttributionProvider = (props) => BUILD_CAPABILITIES.advertising ? (_jsx(EnabledSingularAttributionProvider, { ...props })) : (_jsx(_Fragment, { children: props.children }));
//# sourceMappingURL=SingularAttributionProvider.js.map